import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * Speaker Selector Component
 * 
 * Dropdown for selecting and managing speakers
 */
const SpeakerSelector = ({
  value,
  speakers = [],
  onChange,
  onAddNew,
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  /**
   * Get speaker color based on name
   */
  const getSpeakerColor = (speaker) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800',
      'bg-yellow-100 text-yellow-800',
      'bg-red-100 text-red-800',
    ];

    const hash = speaker.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  /**
   * Filter speakers by search query
   */
  const filteredSpeakers = speakers.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * Handle speaker selection
   */
  const handleSelect = (speaker) => {
    onChange(speaker);
    setIsOpen(false);
    setSearchQuery('');
    setIsAddingNew(false);
  };

  /**
   * Handle add new speaker
   */
  const handleAddNew = () => {
    if (!searchQuery.trim()) return;

    onAddNew?.(searchQuery.trim());
    onChange(searchQuery.trim());
    setSearchQuery('');
    setIsOpen(false);
    setIsAddingNew(false);
  };

  /**
   * Handle click outside
   */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setIsAddingNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Focus input when adding new
   */
  useEffect(() => {
    if (isAddingNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingNew]);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Selected Speaker Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
          transition-all
          ${getSpeakerColor(value || 'UNKNOWN')}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}
        `}
      >
        {value || 'Select Speaker'}
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search or add speaker..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleAddNew();
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Speakers List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredSpeakers.length > 0 ? (
              filteredSpeakers.map((speaker) => (
                <button
                  key={speaker.name}
                  onClick={() => handleSelect(speaker.name)}
                  className={`
                    w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors
                    flex items-center justify-between
                  `}
                >
                  <span className="text-sm font-medium text-gray-900">
                    {speaker.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {speaker.count} {speaker.count === 1 ? 'block' : 'blocks'}
                  </span>
                </button>
              ))
            ) : searchQuery.trim() ? (
              /* Add New Speaker Option */
              <button
                onClick={handleAddNew}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">
                  Add "{searchQuery}"
                </span>
              </button>
            ) : (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No speakers found
              </div>
            )}
          </div>

          {/* Add New Button */}
          {!searchQuery && (
            <div className="p-2 border-t border-gray-200">
              <button
                onClick={() => setIsAddingNew(true)}
                className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add New Speaker
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeakerSelector;


